import { Service } from '@timing71/common';
import { RaceControlMessage } from '@timing71/common/messages';

import { createSignalRConnection } from '../signalr.js';
import { getManifest, translate } from './translate.js';
import { findTimetabledSession } from './utils.js';

const HOST = `livetiming.formula${ (6 / 2) - 2 }.com`;

const EVENT_DATA_URL = `https://${HOST.replace('livetiming', 'api')}/v1/event-tracker`;

// Perform an IN PLACE patch
const patch = (orig, p) => {
  if (p._kf) {
    if (Array.isArray(orig)) {
      orig.length = 0;
    }
    else {
      Object.keys(orig).forEach(
        k => {
          if (k[0] !== '_') {
            delete orig[k];
          }
        }
      );
    }

    delete p._kf;
  }

  Object.entries(p).forEach(
    ([key, value]) => {
      if (key === '_deleted') {
        value.forEach(
          deleted => {
            delete orig[deleted];
          }
        );
      }
      else {
        if (orig[key] !== undefined) {
          if (value === null) {
            delete orig[key];
          }
          else if (typeof(value) === 'object') {
            patch(orig[key], value);
          }
          else {
            orig[key] = value;
          }
        }
        else {
          orig[key] = value;
        }
      }
    }
  );
};

export class SoftPauer extends Service {

  constructor(...args) {
    super(...args);

    this._state = {};
    this._handlePacket = this._handlePacket.bind(this);
    this._raceControlTimestamp = Date.now();
    this._initEventData = this._initEventData.bind(this);

    this._initTimeout = null;
  }

  _handlePacket(pkt) {

    let handled = false;

    if (pkt.M) {
      pkt.M.forEach(
        m => this._handleMessage(...m.A)
      );
      handled = true;
    }

    if (pkt.R) {
      Object.entries(pkt.R).forEach(
        ([key, value]) => {
          this._handleMessage(key, value);
        }
      );
      handled = true;
    }

    if (handled) {
      this.onManifestChange(getManifest(this._state));
      const newState = translate(this._state);

      const messages = this._state.RaceControlMessages?.Messages || []
      const oldTimestamp = this._raceControlTimestamp;

      const newMessages = messages.filter(
        m => new Date(`${m.Utc}Z`).getTime() > oldTimestamp
      ).reverse();

      if (newMessages.length > 0) {
        this._raceControlTimestamp = Math.max(
          ...newMessages.map(
            m => new Date(`${m.Utc}Z`).getTime()
          )
        );
      }

      this.onStateChange({
        ...newState,
        extraMessages: newMessages.map(m => new RaceControlMessage(m.Message, new Date(`${m.Utc}Z`).getTime()).toCTDFormat())
      });
    }
  }

  _handleMessage(msgType, data) {
    if (!this._state[msgType]) {
      this._state[msgType] = {};
    }
    patch(this._state[msgType], data);
  }

  _initEventData() {
    this.connectionService.fetch(
      `${EVENT_DATA_URL}?_=${Date.now()}`,
      {
        headers: {
          Apikey: 'lfjBG5SiokAAND3ucpnE9BcPjO74SpUz',
          Locale: 'en'
        }
      }).then(
      (d) => {
        const data = !!d ? JSON.parse(d) : {};
        this._handleMessage(
          'EventData',
          data
        );

        if (!findTimetabledSession(data.seasonContext?.timetables)) {
          // No active session, try again in 30 seconds?
          this._initTimeout = setTimeout(
            this._initEventData,
            30000
          );
        }
      }
    );
  }

  start(connectionService) {
    super.start(connectionService);

    this._initEventData();

    createSignalRConnection(
      connectionService,
      HOST,
      'signalr',
      'streaming',
      '1.5',
      this.service.uuid
    ).then(
      ({ socket }) => {
        this._socket = socket;

        socket.on(
          'open',
          () => {
            this.emitInfo(null, "Connected to upstream timing source");
            socket.send(
              JSON.stringify({
                H: 'streaming',
                M: 'Subscribe',
                I: 0,
                A: [
                  [
                    'DriverList',
                    'TimingData',
                    'TimingAppData',
                    'ExtrapolatedClock',
                    'LapCount',
                    'RaceControlMessages',
                    'SessionStatus',
                    'TrackStatus',
                    'WeatherData'
                  ]
                ]
              })
            );
          }
        );

        socket.on(
          'message',
          (rawMsg) => {
            if (rawMsg.data) {
              this._handlePacket(
                JSON.parse(rawMsg.data)
              );
            }
            else {
              this._handlePacket(
                JSON.parse(rawMsg.toString())
              );
            }
          }
        );

      }
    );
  }

  stop() {
    this._socket && this._socket.close();
    this._initTimeout && clearTimeout(this._initTimeout);
  }

}

SoftPauer.regex = new RegExp(HOST.replace('livetiming', '[^.]*'));
SoftPauer.private = true;
